import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import {
  decideEmsApprovalApi,
  downloadEmsActionsCsvApi,
  fetchEmsActionsApi,
  requestEmsApprovalApi,
  verifyEmsClosureApi,
} from "../../lib/api";

function approvalBadgeClass(status) {
  return `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
}

function closureBadgeClass(verified) {
  return verified ? "badge badge-closure-verified" : "badge badge-closure-not_verified";
}

export default function ActionsDeadlines() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actingApprovalId, setActingApprovalId] = useState("");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    total_open: 0,
    overdue: 0,
    due_this_month: 0,
    modules_with_open: 0,
  });
  const [filterModule, setFilterModule] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token") || "";
  const isImpersonatedSession =
    token.startsWith("impersonate::") || token.startsWith("impersonate.v1.");
  const clientName = localStorage.getItem("client");

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchEmsActionsApi();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || {
        total_open: 0,
        overdue: 0,
        due_this_month: 0,
        modules_with_open: 0,
      });
    } catch (err) {
      toast.error(err?.message || "Failed to load actions and deadlines");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const moduleOptions = useMemo(() => {
    const set = new Set((items || []).map((it) => String(it.module_name || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const ownerOptions = useMemo(() => {
    const set = new Set((items || []).map((it) => String(it.owner || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return (items || []).filter((it) => {
      if (filterModule !== "all" && String(it.module_name || "") !== filterModule) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly && !it.is_overdue) return false;
      if (dueThisMonthOnly && !it.due_this_month) return false;
      return true;
    });
  }, [items, filterModule, filterOwner, overdueOnly, dueThisMonthOnly]);

  const applyPreset = (preset) => {
    if (preset === "overdue") {
      setFilterModule("all");
      setFilterOwner("all");
      setOverdueOnly(true);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterModule("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    if (preset === "unassigned_owner") {
      setFilterModule("all");
      setFilterOwner("__unassigned__");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    setFilterModule("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadEmsActionsCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ems_actions_open_items.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Actions exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export actions");
    } finally {
      setExporting(false);
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setActingApprovalId(item.action_id);
      await requestEmsApprovalApi({
        module_key: item.module_key,
        item_id: item.item_id,
        notes: "",
        target_client: clientName || "",
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setActingApprovalId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setActingApprovalId(item.action_id);
      await decideEmsApprovalApi({
        module_key: item.module_key,
        item_id: item.item_id,
        decision,
        notes: "",
        target_client: clientName || "",
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to submit decision");
    } finally {
      setActingApprovalId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setActingApprovalId(item.action_id);
      await verifyEmsClosureApi({
        module_key: item.module_key,
        item_id: item.item_id,
        notes: "",
        target_client: clientName || "",
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setActingApprovalId("");
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>Actions & Deadlines</h1>
          <p className="subtitle">Central view of open actions across all EMS modules.</p>
        </div>
        <div className="dashboard-header-actions">
          <button className="btn-secondary" onClick={handleExportCsv} disabled={loading || exporting}>
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="summary-row">
        <div className="kpi-card">
          <div className="kpi-label">Open actions</div>
          <h2>{loading ? "-" : Number(summary.total_open || 0)}</h2>
          <div className="kpi-sub">Across all modules</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Overdue</div>
          <h2>{loading ? "-" : Number(summary.overdue || 0)}</h2>
          <div className="kpi-sub">Past due date</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Due this month</div>
          <h2>{loading ? "-" : Number(summary.due_this_month || 0)}</h2>
          <div className="kpi-sub">Current month</div>
        </div>
      </div>

      <div className="filter-bar" style={{ gridTemplateColumns: "220px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Module</span>
          <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)}>
            <option value="all">all</option>
            {moduleOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("overdue")}>Only overdue</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("unassigned_owner")}>Unassigned owner</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Module</th>
              <th>Title</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Due date</th>
              <th>Evidence</th>
              <th>Source</th>
              <th>Approval Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={9} className="table-empty">No open actions found.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.action_id}>
                  <td>{it.module_name || "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.owner || "-"}</td>
                  <td>{it.status || "-"}</td>
                  <td>
                    <span className={approvalBadgeClass(it.approval_status)}>
                      {it.approval_status || "not_requested"}
                    </span>
                    {it.closure_verification_required ? (
                      <span className={closureBadgeClass(Boolean(it.closure_verified))} style={{ marginLeft: 6 }}>
                        {it.closure_verified ? "verified" : "not_verified"}
                      </span>
                    ) : null}
                  </td>
                  <td>{it.due_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>
                    <Link to={it.route || "/dashboard"}>Open module</Link>
                  </td>
                  <td>
                    {role === "admin" || isImpersonatedSession ? (
                      it.review_eligible ? (
                        <>
                          <button
                            className="admin-btn secondary"
                            style={{ marginRight: 8 }}
                            onClick={() => handleDecision(it, "review")}
                            disabled={actingApprovalId === it.action_id}
                          >
                            Review
                          </button>
                          <button
                            className="admin-btn danger"
                            onClick={() => handleDecision(it, "reject")}
                            disabled={actingApprovalId === it.action_id}
                          >
                            Reject
                          </button>
                        </>
                      ) : it.approve_eligible ? (
                        <>
                          <button
                            className="admin-btn secondary"
                            style={{ marginRight: 8 }}
                            onClick={() => handleDecision(it, "approve")}
                            disabled={actingApprovalId === it.action_id}
                          >
                            Approve
                          </button>
                          <button
                            className="admin-btn danger"
                            onClick={() => handleDecision(it, "reject")}
                            disabled={actingApprovalId === it.action_id}
                          >
                            Reject
                          </button>
                        </>
                      ) : it.verify_close_eligible ? (
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleVerifyClose(it)}
                          disabled={actingApprovalId === it.action_id}
                        >
                          Verify close
                        </button>
                      ) : (
                        <span>-</span>
                      )
                    ) : (
                      <button
                        className="admin-btn secondary"
                        onClick={() => handleRequestApproval(it)}
                        disabled={
                          actingApprovalId === it.action_id ||
                          it.approval_status === "pending" ||
                          it.approval_status === "pending_review" ||
                          !it.approval_eligible
                        }
                      >
                        {it.approval_status === "pending"
                          ? "Pending"
                          : it.approval_status === "pending_review"
                            ? "In review"
                            : "Request"}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
